import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboard, isForbidden } from '@cwos/api-client';
import type { dashboard as DashboardNS } from '@cwos/api-client';
import { Refused, SkeletonSet, type Column } from '@cwos/ui-admin';
import { useAnalyticsControls } from '../AnalyticsContext.js';
import { askPeriod, formatNumber, periodRangeLabel } from '../analyticsRange.js';
import { DrillPanel, WeeklyPanel, type Drill, TrendFailure } from '../drilldown.js';
import { METRIC_ABOUT, type MetricAboutKey } from '../explain.js';
import {
  DrilldownTable,
  MeasureStat,
  Panel,
  Stat,
  StatGrid,
  StalenessStrip,
  type QueryLike,
} from '../shared.js';
import { measure, SUPPRESSED_LABEL } from '../disclosure.js';
import { LoadFailure } from '../../../LoadFailure.js';
import { explainDraft } from '../../../intelligence/global/index.js';

type ImpactByProgramRow = DashboardNS.ImpactByProgramRow;

export function ImpactView() {
  const { apiQuery } = useAnalyticsControls();
  const [drill, setDrill] = useState<Drill | null>(null);

  const impact = useQuery({
    queryKey: ['analytics', 'impact', apiQuery],
    queryFn: () => dashboard.analyticsImpact(apiQuery),
  });

  // The same period as the figures above it (it used to be a fixed 26 weeks).
  const trends = useQuery({
    queryKey: ['analytics', 'trends', apiQuery],
    queryFn: () => dashboard.analyticsTrends(apiQuery),
  });

  // A 403 is an answer. It was taking the failure branch and getting a retry
  // button that cannot change it.
  if (impact.isError && isForbidden(impact.error)) {
    return (
      <Refused
        heading="This view is not shown to you"
        message="Your account does not have access to the figures behind it. Nothing has been left out of them — they are not yours to see."
      />
    );
  }
  if (impact.isLoading) return <SkeletonSet surface="table" rows={4} />;
  if (impact.isError && !impact.data) {
    return (
      <LoadFailure
        error={impact.error}
        message="We couldn’t load impact analytics just now, so read this screen as unknown rather than empty."
        onRetry={() => void impact.refetch()}
      />
    );
  }
  const d = impact.data;
  // `return null` rendered a BLANK VIEW — the reader saw a page with nothing on
  // it and no reason given. A query that has not answered is pending.
  if (!d) return <SkeletonSet surface="table" rows={4} />;

  /** Every delivery figure here counts the canonical delivered visits of the period. */
  const open = (label: string, key: MetricAboutKey) => () =>
    setDrill({
      title: `${label}, ${periodRangeLabel(d.period)}`,
      about: METRIC_ABOUT[key],
      ask: explainDraft(label.toLowerCase(), askPeriod(d.period)),
      records: { set: 'delivered', from: d.period.from, to: d.period.to, scope: d.scope },
    });

  const columns: Column<ImpactByProgramRow>[] = [
    {
      key: 'program',
      header: 'Program',
      render: (r) => <span style={{ fontWeight: 600 }}>{r.programName}</span>,
    },
    {
      key: 'clients',
      header: 'Clients served',
      render: (r) => String(r.clientsServed),
      numeric: true,
    },
    {
      // PER ROW. This table was the sharpest edge of the whole gap: it printed
      // `hours` and `appointments` in the same row as `Clients served` and
      // `Workers`, so a row reading "1 client, 1 worker, 12.5 hours" named two
      // people and told the reader how few they were. The headcount columns
      // stay — they explain the blank; the measures beside them do not.
      key: 'appointments',
      header: 'Appointments',
      render: (r) => (r.appointments == null ? SUPPRESSED_LABEL : String(r.appointments)),
      numeric: true,
    },
    {
      key: 'hours',
      // Ritten-backed service minutes — never "hours worked" (timekeeping).
      header: 'Service hours delivered',
      render: (r) => (r.hours == null ? SUPPRESSED_LABEL : formatNumber(r.hours, 1)),
      numeric: true,
    },
    {
      key: 'workers',
      header: 'Workers',
      render: (r) => String(r.uniqueWorkers),
      numeric: true,
    },
  ];

  return (
    <div className="an-stack">
      <StatGrid>
        {/*
          THE TWO HEADCOUNTS STAY BARE and the two measures go through
          `measure()`. `clientsServed` and `uniqueWorkers` ARE the cohorts, so
          they are published — and they are what makes the blanks beside them
          legible rather than mysterious.
        */}
        <Stat
          label="Clients served"
          value={String(d.clientsServed)}
          onOpen={open('Clients served', 'clientsServed')}
        />
        <MeasureStat
          label="Services delivered"
          display={measure(d.appointments, 'appointments', d.disclosure, String)}
          onOpen={open('Services delivered', 'servicesDelivered')}
        />
        <MeasureStat
          label="Service hours delivered"
          display={measure(d.totalHours, 'totalHours', d.disclosure, (v) => formatNumber(v, 1))}
          onOpen={open('Service hours delivered', 'serviceHours')}
        />
        <Stat
          label="Workers delivering service"
          value={String(d.uniqueWorkers)}
          onOpen={open('Workers delivering service', 'workersDelivering')}
        />
        <Stat label="Programs active" value={String(d.programsActive)} />
      </StatGrid>

      {drill ? <DrillPanel key={drill.title} drill={drill} onClose={() => setDrill(null)} /> : null}

      <StalenessStrip staleness={d.staleness} />

      <ImpactTrendPanel trends={trends} onDrill={setDrill} />

      <Panel title="By program" subtitle="outreach across programs">
        <DrilldownTable
          rows={d.byProgram}
          isLoading={false}
          isError={false}
          onRetry={() => void impact.refetch()}
          columns={columns}
          rowKey={(r) => r.programId ?? r.programName}
          emptyHeading="No outreach yet"
          emptyMessage="Program activity appears once service events are recorded."
        />
      </Panel>
    </div>
  );
}

function ImpactTrendPanel({
  trends,
  onDrill,
}: {
  trends: QueryLike<DashboardNS.AnalyticsTrends>;
  onDrill: (drill: Drill) => void;
}) {
  if (trends.isLoading) return <SkeletonSet surface="band" />;
  if (trends.isError && !trends.data) {
    return (
      <TrendFailure
        error={trends.error}
        message="We couldn’t load impact trends just now."
        onRetry={() => void trends.refetch()}
      />
    );
  }
  const t = trends.data;
  if (!t) return null;
  return (
    <div className="an-grid">
      <WeeklyPanel
        title="Clients served"
        axis={t}
        about={METRIC_ABOUT.clientsServed}
        onDrill={onDrill}
        lines={[
          {
            name: 'Clients served',
            color: 'var(--chart-3)',
            series: t.series.clientsServed,
            set: 'delivered',
          },
        ]}
      />
      {/* SERVICE hours, beside the service-hours figure above. This panel drew
          the TIME CLOCK's hours, a different population, which read 0 at an
          organization with no time clock beside 2,681 service hours. */}
      <WeeklyPanel
        title="Service hours delivered"
        axis={t}
        about={METRIC_ABOUT.serviceHours}
        additive
        formatValue={(v) => formatNumber(v, Number.isInteger(v) ? 0 : 1)}
        onDrill={onDrill}
        lines={[
          {
            name: 'Service hours delivered',
            color: 'var(--chart-1)',
            series: t.series.serviceHours,
            set: 'delivered',
          },
        ]}
      />
    </div>
  );
}
